import i18n from "@microdoing/shared/i18n";
import { changeDonePercentage } from "@microdoing/shared/services/action/actions";
import { getDonePercentage } from "@microdoing/shared/services/action/selectors";
import { useNavigation } from "@react-navigation/native";
import {
  Box,
  MainButton,
  MainText,
  MySafeAreaView,
} from "app/components/common";
import HeaderScreen from "app/components/common/HeaderScreen";
import FeelingQuestionIcon from "app/components/Icons/Images/FeelingQuestion";
import theme from "app/Theme/Theme";
import normalize from "app/utils/normalize";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SmileyFeeling from "./SmileyFeelings";
import { Dimensions, Image } from "react-native";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import { breadCrumbsReflexiveAnalysisFeelingScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import { feelingSmiley } from "app/utils/getRating";

const dimensions = Dimensions.get("window");
const SMILEY_BOX_SIZE =
  dimensions.height >= theme.breakpoints.smallWeb.height &&
  dimensions.width >= theme.breakpoints.smallWeb.width &&
  dimensions.height < theme.breakpoints.web.height
    ? 180
    : normalize(190);

export interface IActionFeelingScreenProps {}

const ActionFeelingScreen = ({ ...props }: IActionFeelingScreenProps) => {
  const [feeling, setFeeling] = useState("");

  const donePercentage = useSelector(getDonePercentage);

  const navigation = useNavigation();
  const dispatch = useDispatch();

  const timeoutRef = useRef<any>(null);

  useEffect(() => {
    switch (donePercentage) {
      case 25: {
        setFeeling("veryHard");
        break;
      }
      case 50: {
        setFeeling("hard");
        break;
      }
      case 75: {
        setFeeling("easy");
        break;
      }
      case 100: {
        setFeeling("veryEasy");
        break;
      }
      default:
        break;
    }
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const onSetFeelings = newFeeling => () => {
    setFeeling(newFeeling);
    let percentageDone = 75;
    switch (newFeeling) {
      case "veryHard": {
        percentageDone = 25;
        break;
      }
      case "hard": {
        percentageDone = 50;
        break;
      }
      case "easy": {
        percentageDone = 75;
        break;
      }
      case "veryEasy": {
        percentageDone = 100;
        break;
      }
      default:
        break;
    }

    dispatch(changeDonePercentage(percentageDone));
  };

  return (
    <WebDesktopView breadcrumbsPath={breadCrumbsReflexiveAnalysisFeelingScreen}>
      <MySafeAreaView
        flex={1}
        borderRadius={isWebDesktop ? 8 : 0}
        padding={isWebDesktop ? "xl" : "null"}
        paddingHorizontal={isWebDesktop ? "xxl" : "sl"}
        paddingTop={isWebDesktop ? "null" : "s"}
        backgroundColor="neutral.white"
        zIndex={0}
        alignItems="center"
        overflow="visible"
      >
        {!isWebDesktop && (
          <HeaderScreen title={i18n.t("action-feeling")} leftIcon />
        )}
        <Box flex={1} width={isWebDesktop ? "45%" : "100%"}>
          <Box flex={1} justifyContent="flex-start" alignItems="center">
            <Box flex={1} alignItems="center" justifyContent="center">
              <Box flex={1} justifyContent="center" alignItems="center">
                {!!feeling ? (
                  <Box
                    height={SMILEY_BOX_SIZE}
                    width={SMILEY_BOX_SIZE}
                    borderRadius={SMILEY_BOX_SIZE / 2}
                    backgroundColor="neutral.50"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Image
                      style={{
                        width: normalize(170),
                        height: normalize(170),
                      }}
                      source={feelingSmiley[feeling]}
                    />
                  </Box>
                ) : (
                  <FeelingQuestionIcon
                    height={SMILEY_BOX_SIZE}
                    width={SMILEY_BOX_SIZE}
                  />
                )}
              </Box>
              <Box justifyContent="center" alignItems="center">
                <Box marginBottom="xs">
                  {!!feeling ? (
                    <MainText variant="l5-bold" textAlign="center">
                      {i18n.t(`feeling-text-${feeling}`)}
                    </MainText>
                  ) : (
                    <MainText variant="l5-bold" textAlign="center">
                      {i18n.t("action-feeling-question")}
                    </MainText>
                  )}
                </Box>
                <MainText
                  color="neutral.600"
                  variant="l4-medium"
                  textAlign="center"
                >
                  {i18n.t("action-feeling-explanation")}
                </MainText>
              </Box>
            </Box>
          </Box>
          <Box
            flexDirection="row"
            justifyContent="space-between"
            marginBottom="s"
            marginTop="l"
          >
            <SmileyFeeling
              isSelected={feeling === "veryHard"}
              smiley={feelingSmiley.veryHard}
              text={i18n.t(`feeling-text-veryHard`)}
              setFeeling={onSetFeelings("veryHard")}
            />
            <SmileyFeeling
              isSelected={feeling === "hard"}
              smiley={feelingSmiley.hard}
              text={i18n.t(`feeling-text-hard`)}
              setFeeling={onSetFeelings("hard")}
            />
            <SmileyFeeling
              isSelected={feeling === "easy"}
              smiley={feelingSmiley.easy}
              text={i18n.t(`feeling-text-easy`)}
              setFeeling={onSetFeelings("easy")}
            />
            <SmileyFeeling
              isSelected={feeling === "veryEasy"}
              smiley={feelingSmiley.veryEasy}
              text={i18n.t(`feeling-text-veryEasy`)}
              setFeeling={onSetFeelings("veryEasy")}
            />
          </Box>
        </Box>
        <Box width={isWebDesktop ? "45%" : "100%"}>
          <MainButton
            isDisabled={!feeling}
            onPress={() => navigation.navigate("ActionRatingScreen")}
            text={i18n.t("next")}
          />
        </Box>
      </MySafeAreaView>
    </WebDesktopView>
  );
};

export default ActionFeelingScreen;
